import { useState } from 'react'
import styles from '@/components/directory/v2/list-view/EVsListItemVariantComponent.module.css'
import Link from 'next/link'
import PriceIndicator from '@/components/ui/attributes/v2/PriceIndicator'

const EVsListItemVariantComponent = ({ variant }) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [zoomedImgVisible, setZoomedImgVisible] = useState(false)
  const [zoomedImgPosition, setZoomedImgPosition] = useState({ top: 0, left: 0 })

  const {
    slug,
    make,
    model,
    year,
    vehicle_id,
    description,
    images,
    attributes,
    basic_details,
    price_category,
    status,
    min_price_category,
    max_price_category,
  } = variant

  if (!variant) {
    return null // or a loading indicator
  }

  const getStatusText = (statusCode) => {
    switch (statusCode) {
      case 0:
        return 'Available used'
      case 1:
        return 'Available new'
      case 2:
        return 'Pre-order'
      case 3:
        return 'Coming soon'
      case 91:
        return 'Coming soon'
      default:
        return 'Concept car' // Fallback for unknown codes
    }
  }

  const getStatusClass = (statusCode) => {
    switch (statusCode) {
      case 0:
        return styles.availableUsed
      case 1:
        return styles.availableNew
      case 2:
        return styles.preOrder
      case 3:
      case 91:
        return styles.comingSoon
      default:
        return styles.conceptCar
    }
  }

  // Determine the source of the image
  const hasImages = Array.isArray(images) && images.length > 0
  const imageUrl = hasImages ? images[0].image_url : '/illustrations/v2/EV-v1.png'
  const imageClassName = hasImages ? styles.cardImage : styles.fallBackImage
  const containerClassName = hasImages ? styles.imageContainer : styles.fallbackImageContainer

  // Image loading event handlers
  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  // Mouse event handlers for zoom
  const handleMouseEnter = () => {
    //setZoomedImgVisible(true);
    setZoomedImgVisible(false) // Disable zoom for now
  }

  const handleMouseMove = (e) => {
    const zoomedImgWidth = 420 // Width of the zoomed image
    const zoomedImgHeight = 300 // Estimated height of the zoomed image
    const offsetX = 20 // Offset from the mouse pointer
    const offsetY = 20 // Offset from the mouse pointer

    // Calculate position relative to the viewport
    let top = e.offsetY + offsetY
    let left = e.offsetX + offsetX

    // Ensure the zoomed image stays within the viewport horizontally
    if (left + zoomedImgWidth > window.innerWidth) {
      left = e.offsetX - zoomedImgWidth - offsetX
    }

    // Ensure the zoomed image stays within the viewport vertically
    if (top + zoomedImgHeight > window.innerHeight) {
      top = e.offsetY - zoomedImgHeight - offsetY
    }

    setZoomedImgPosition({ top, left })
  }

  const handleMouseLeave = () => {
    setZoomedImgVisible(false)
  }

  // Helper function to determine display value and unit
  const getDisplayValues = (attr) => {
    if (attr.unit === 'null' || attr.unit === null) {
      return { displayValue: attr.value, displayUnit: '' }
    } else {
      return { displayValue: attr.value, displayUnit: attr.unit }
    }
  }

  const { displayValue: displayValue1, displayUnit: displayUnit1 } = getDisplayValues(attributes[1])

  return (
    <Link href={`/directory/${slug}`} className={styles.container}>
      <div className={styles.picture}>
        <div
          className={containerClassName}
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {!imageLoaded && <div className={styles.loader}></div>}
          <img
            className={imageClassName}
            src={imageUrl}
            alt={`${make} ${model}`}
            onLoad={handleImageLoad}
            style={{ display: imageLoaded ? 'block' : 'none' }}
          />
        </div>
        {zoomedImgVisible && (
          <img
            src={imageUrl}
            alt={`${make} ${model}`}
            className={styles.zoomedImage}
            style={{
              top: `${zoomedImgPosition.top}px`,
              left: `${zoomedImgPosition.left}px`,
            }}
          />
        )}
      </div>
      <div className={styles.group1}>
        <div className={styles.name}>{`${make} ${model}`}</div>
        <div className={styles.variant}>{basic_details[2].value}</div>
      </div>
      <div className={styles.attributes}>
        <div className={styles.attribute}>
          <div className={styles.attributeData}>
            <span className={styles.attributeValue}>{year}</span>
          </div>
          <div className={styles.attributeName}>Year</div>
        </div>
        <div className={styles.attribute}>
          <div className={styles.attributeData}>
            <span className={styles.attributeValue}>{basic_details[4].value}</span>{' '}
            <span className={styles.attributeUnit}>{basic_details[4].unit}</span>
          </div>
          <div className={styles.attributeName}>Capacity</div>
        </div>
        <div className={styles.attribute}>
          <div className={styles.attributeData}>
            <span className={`${styles.attributePill} ${getStatusClass(status)}`}>{getStatusText(status)}</span>
          </div>
          <div className={styles.attributeName}>Status</div>
        </div>
        {/*<div className={styles.attribute}>
          <div className={styles.attributeData}>
            <span className={styles.attributeValue}>
              <PriceIndicator min_price_category={min_price_category} max_price_category={max_price_category} />
            </span>
          </div>
          <div className={styles.attributeName}>Price</div>
        </div>*/}
        <div className={styles.attribute}>
          <div className={styles.attributeData}>
            <span className={styles.attributeValue}>{attributes[0].value}</span>
            <span className={styles.attributeUnit}>{attributes[0].unit}</span>
          </div>
          <div className={styles.attributeName}>{attributes[0].attribute}</div>
        </div>
        <div className={styles.attribute}>
          <div className={styles.attributeData}>
            <span className={styles.attributeValue}>{displayValue1}</span>
            {displayUnit1 && <span className={styles.attributeUnit}>{displayUnit1}</span>}
          </div>
          <div className={styles.attributeName}>{attributes[1].attribute}</div>
        </div>
        <div className={styles.attribute}>
          <div className={styles.attributeData}>
            <span className={styles.attributeValue}>{attributes[2].value}</span>
            <span className={styles.attributeUnit}>{attributes[2].unit}</span>
          </div>
          <div className={styles.attributeName}>{attributes[2].attribute}</div>
        </div>
      </div>
    </Link>
  )
}

export default EVsListItemVariantComponent
